import React from "react";
import {useSelector} from "react-redux";
import {Formik, FormikHelpers} from "formik";
import {Button, Card, CardContent} from "@material-ui/core";
import * as yup from "yup";
import {GiftorDetails} from "./GiftorDetails";
import GiftorPrimaryDetails from "./GiftorPrimaryDetails";
import {assetsFields} from "./Assets";
import {liabilitiesFields} from "./Liabilities";
import {
    GwtAssetsProps,
    GwtDocsProps,
    GwtLiabilitiesProps,
    GwtNetWorth,
    GWTPolicyDetails,
    InviteRecipientsProps
} from "../../../model/giftor";
import {fields, getDocument, getDocumentsSetting} from "../../../pages/client/Kids/util";
import {RootState} from "../../../reducers";
import {useActions} from "../../../actions";
import * as GiftorActions from "../../../actions/giftor";
import {GiftorStatus} from "../utils";
import {
    checkRole,
    isDebug,
    isFunc,
    pez,
    sleep,
    unmaskCurrency,
    unmaskPhoneNumber, va,
    validObj
} from "../../../utils/commonUtils";
import {DateOfBirthWith18Years} from "../../../utils/validation";
import {
    AccountType,
    ALPHABET_NUMERIC_ONLY,
    ALPHABET_ONLY,
    ALPHABET_WITH_HYPHEN_DOT_REGEX,
    ALPHABET_WITH_NUMBER,
    ClientDocumentType,
    NationalLifeCarrierDetail,
    REQUIRED_FIELD,
} from "../../../constants";


interface CGPType extends GwtAssetsProps, GwtDocsProps, GwtLiabilitiesProps, GwtNetWorth, GWTPolicyDetails, InviteRecipientsProps {
    phoneNumber: string
}

type GiftorInvitationProps = {
    clientDetail?: any;
    giftorStatus?: any;
    handleEditStatus: Function;
    setGiftorStatus?: Function;
    setLoading: Function;
}


export default function GiftorInvitation(props: GiftorInvitationProps) {
    const {clientDetail, giftorStatus, handleEditStatus, setGiftorStatus, setLoading} = props;
    const user = useSelector((state: RootState) => state.profile);
    const userData = clientDetail ? clientDetail : user;
    const giftorActions = useActions(GiftorActions);
    const role = user && user.role;
    const submitButton = checkRole(role, [AccountType.Agent, AccountType.Individual, AccountType.Multilife]);
    const isNotIndividualOrMultiLife = !checkRole(user.role, [AccountType.Individual, AccountType.Multilife]);
    const clientPolicy = userData && userData.client && userData.client.clientPolicies && userData.client.clientPolicies[0];
    const enrollment = clientPolicy && clientPolicy.enrollment;
    const [initVal, setInitVal] = React.useState<any>({});
    const debugMode = isDebug();
    const createKeyValidation = () => {
        let keyValidate = {};
        keyValidate[fields.firstName] = yup.string().trim().required(REQUIRED_FIELD);
        keyValidate[fields.lastName] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.email] = yup.string().email("Invalid email").required(REQUIRED_FIELD);
        keyValidate[fields.phoneNumber] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.dob] = DateOfBirthWith18Years("Giftor should be 18-years or older");
        keyValidate[fields.gender] = yup.string().required(REQUIRED_FIELD).nullable();
        keyValidate[fields.employeeSSN] = yup.string().trim().matches(/^\d{3}-\d{2}-\d{4}$/, REQUIRED_FIELD).nullable()
                .test('employeeSSN', REQUIRED_FIELD, value => {
                    if (!value) return false;
                    const cleanedSSN = value.replace(/\D/g, '');
                    return cleanedSSN.length === 9;
                }).nullable();


        // IndividualIncome Section only for National Life Carrier
        if (clientPolicy && clientPolicy.carrierId === NationalLifeCarrierDetail.id) {
            keyValidate["indIncome1YearsPriorAnnualSalary"] = yup.string().required(REQUIRED_FIELD).nullable();
            keyValidate["indIncome2YearsPriorAnnualSalary"] = yup.string().required(REQUIRED_FIELD).nullable();
        }

        // IndividualIncome Section/
        keyValidate[fields.indIncomeLastYearAnnualSalary] = yup.string().required(REQUIRED_FIELD).nullable();
        keyValidate[fields.netWorth] = yup.string().required(REQUIRED_FIELD).nullable();

        // verify Assets
        keyValidate[fields.verifyAssets] = yup.boolean().oneOf([true], "Please verify your assets").required("Please verify your assets");

        // verify Liabilities
        keyValidate[fields.verifyLiabilities] = yup.boolean().oneOf([true], "Please verify your liabilities").required("Please verify your liabilities");

        // insurance coverage etc
        keyValidate[fields.currentKaiZenPolicy] = yup.boolean().required(REQUIRED_FIELD);
        keyValidate[fields.hasCurrentInsuranceCoverage] = yup.boolean().when(fields.currentKaiZenPolicy, {
            is: (val) => val === "1",
            then: yup.boolean().required(REQUIRED_FIELD),
            otherwise: yup.boolean().notRequired(),
        });

        keyValidate[fields.gen1ICN] = yup.string().when(fields.currentKaiZenPolicy, {
            is: (val) => val === true,
            then: yup.string().trim().matches(ALPHABET_WITH_HYPHEN_DOT_REGEX, ALPHABET_ONLY).required(REQUIRED_FIELD).nullable(),
            otherwise: yup.string().notRequired(),
        });

        keyValidate[fields.gen1DeathBAP] = yup.string().when(fields.currentKaiZenPolicy, {
            is: (val) => val === true,
            then: yup.string().required(REQUIRED_FIELD).nullable(),
            otherwise: yup.string().notRequired(),
        });
        keyValidate[fields.giftorInsurable] = yup.boolean().when(fields.hasCurrentInsuranceCoverage, {
            is: (val) => val === false,
            then: yup.boolean().required(REQUIRED_FIELD),
            otherwise: yup.boolean().notRequired(),
        });
        keyValidate[fields.otherCompanyName] = yup.string().when(fields.hasCurrentInsuranceCoverage, {
            is: (val) => val === true,
            then: yup.string().trim().matches(ALPHABET_WITH_HYPHEN_DOT_REGEX, ALPHABET_ONLY).required(REQUIRED_FIELD).nullable(),
            otherwise: yup.string().notRequired(),
        });
        keyValidate[fields.otherPolicyNumber] = yup.string().when(fields.hasCurrentInsuranceCoverage, {
            is: (val) => val === true,
            then: yup.string().trim().matches(ALPHABET_WITH_NUMBER, ALPHABET_NUMERIC_ONLY).notRequired().nullable(),
            otherwise: yup.string().notRequired(),
        });
        keyValidate[fields.gen1PolicyNumber] = yup.string().when(fields.currentKaiZenPolicy, {
            is: (val) => val === true,
            then: yup.string().trim().matches(ALPHABET_WITH_NUMBER, ALPHABET_NUMERIC_ONLY).notRequired().nullable(),
            otherwise: yup.string().notRequired(),
        });

        // has kaizen fields validations
        const keysToValidate = [
            fields.otherDeathBenefitAmount,
            fields.otherType,
        ];
        const validateHasKaizenFields = yup.string().nullable().when(fields.hasCurrentInsuranceCoverage, {
            is: (val) => val === true,
            then: yup.string().trim().required(REQUIRED_FIELD),
            otherwise: yup.string().notRequired(),
        });
        keysToValidate.forEach(key => {
            keyValidate[key] = validateHasKaizenFields;
        });

        // has document validations
        keyValidate[fields.documentType] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.identityDocs] = yup.array().min(2, REQUIRED_FIELD).required(REQUIRED_FIELD);

        keyValidate[fields.taxDocumentType] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.taxDocument] = yup.array().min(2, REQUIRED_FIELD).required(REQUIRED_FIELD);
        return keyValidate;
    };
    const onCloseGiftor = () => {
        setGiftorStatus && isFunc(setGiftorStatus) && setGiftorStatus(!giftorStatus);
        window.scrollTo(0, 0);
    };
    const createGiftorPayload = (data: CGPType) => {
        const payload: any = {};
        payload.phoneNumber = Number(unmaskPhoneNumber(data.phoneNumber || "").toString());

        payload.cashInBank = Number(unmaskCurrency(data.cashInBank || "").toString());
        payload.primaryRealEstate = Number(unmaskCurrency(data.primaryRealEstate || "").toString());
        payload.receivables = Number(unmaskCurrency(data.receivables || "").toString());
        payload.stockBondAndSecurity = Number(unmaskCurrency(data.stockBondAndSecurity || "").toString());
        payload.personalProperty = Number(unmaskCurrency(data.personalProperty || "").toString());
        payload.businessEquity = Number(unmaskCurrency(data.businessEquity || "").toString());
        payload.lifeInsuranceCashValue = Number(unmaskCurrency(data.lifeInsuranceCashValue || "").toString());
        payload.annuities = Number(unmaskCurrency(data.annuities || "").toString());
        payload.otherAssets = Number(unmaskCurrency(data.otherAssets || "").toString());
        payload.otherAssetDetails = data.otherAssetDetails;
        payload.netWorth = Number(unmaskCurrency(data.netWorth || "").toString());

        payload.unpaidInterestTaxes = Number(unmaskCurrency(data.unpaidInterestTaxes || "").toString());
        payload.mortgages = Number(unmaskCurrency(data.mortgages || "").toString());
        payload.loansOnLifeInsurance = Number(unmaskCurrency(data.loansOnLifeInsurance || "").toString());
        payload.personalNotes = Number(unmaskCurrency(data.personalNotes || "").toString());
        payload.accountsPayable = Number(unmaskCurrency(data.accountsPayable || "").toString());
        payload.otherLiabilities = Number(unmaskCurrency(data.otherLiabilities || "").toString());
        payload.otherLiabilitiesDetails = data.otherLiabilitiesDetails;

        payload.indIncomeLastYearAnnualSalary = Number(unmaskCurrency(data.indIncomeLastYearAnnualSalary || "").toString());
        payload.indIncomeLastYearDividends = Number(unmaskCurrency(data.indIncomeLastYearDividends || "").toString());
        payload.indIncomeLastYearOtherIncome = Number(unmaskCurrency(data.indIncomeLastYearOtherIncome || "").toString());
        payload.indIncomeLastYearTotal = Number(unmaskCurrency(data.indIncomeLastYearTotal || "").toString());

        payload.indIncome1YearsPriorAnnualSalary = Number(unmaskCurrency(data.indIncome1YearsPriorAnnualSalary || "").toString());
        payload.indIncome1YearsPriorDividends = Number(unmaskCurrency(data.indIncome1YearsPriorDividends || "").toString());
        payload.indIncome1YearsPriorOtherIncome = Number(unmaskCurrency(data.indIncome1YearsPriorOtherIncome || "").toString());
        payload.indIncome1YearsPriorTotal = Number(unmaskCurrency(data.indIncome1YearsPriorTotal || "").toString());

        payload.indIncome2YearsPriorAnnualSalary = Number(unmaskCurrency(data.indIncome2YearsPriorAnnualSalary || "").toString());
        payload.indIncome2YearsPriorDividends = Number(unmaskCurrency(data.indIncome2YearsPriorDividends || "").toString());
        payload.indIncome2YearsPriorOtherIncome = Number(unmaskCurrency(data.indIncome2YearsPriorOtherIncome || "").toString());
        payload.indIncome2YearsPriorTotal = Number(unmaskCurrency(data.indIncome2YearsPriorTotal || "").toString());

        payload.indOtherIncomeDetail = data.indOtherIncomeDetail || "";

        payload[fields.currentKaiZenPolicy] = data[fields.currentKaiZenPolicy] === "1";
        payload.gen1TranceNo = data[fields.gen1TranceNo];
        payload[fields.gen1ICN] = data[fields.gen1ICN];
        payload[fields.gen1DeathBAP] = Number(unmaskCurrency(pez(data[fields.gen1DeathBAP])).toString());
        payload[fields.gen1PolicyNumber] = data[fields.gen1PolicyNumber];
        payload.hasCurrentInsuranceCoverage = data[fields.hasCurrentInsuranceCoverage] === "1";
        payload.otherCompanyName = data[fields.otherCompanyName];
        payload.otherType = data[fields.otherType];
        payload.otherDeathBenefitAmount = data[fields.otherDeathBenefitAmount];
        payload.otherPolicyNumber = data[fields.otherPolicyNumber];
        payload.giftorInsurable = data[fields.giftorInsurable] === "1";

        return payload;
    };
    const createDocumentPayload = (clientDocuments: any, docSubType: string) => {
        const clientPolicy = userData && userData.client && userData.client.clientPolicies && userData.client.clientPolicies.length > 0 && userData.client.clientPolicies[0];
        const documents: any = [];
        const generateFrontSideDocPayload = (doc: any, isBackPath: boolean = false) => {
            const documentKey = clientPolicy.identityType === "passport" ?
                    (isBackPath ? "uploadPassportBackGEN1GWT" : "uploadPassportGEN1GWT") :
                    (isBackPath ? "uploadDriversLicenseBackGEN1GWT" : "uploadDriversLicenseGEN1GWT");
            return {
                backPath: isBackPath ? doc.backPath : "",
                docSubName: doc.name,
                document: docSubType,
                documentKey,
                documentSubType: doc.documentSubType,
                documentType: doc.documentType,
                path: isBackPath ? doc.backPath : doc.path,
                variant: isBackPath ? "back" : "front",
            };
        };
        clientDocuments.forEach((doc: any) => {
            debugMode && console.info(doc.documentType, doc.documentSubType, doc.path, doc.backPath, doc);
            switch (doc.documentType) {
                case ClientDocumentType.IDENTITY_DOCUMENT:
                    if (doc.path && !doc.backPath) {
                        documents.push(generateFrontSideDocPayload(doc));
                    }
                    if (doc.backPath) {
                        documents.push(generateFrontSideDocPayload(doc));
                        documents.push(generateFrontSideDocPayload(doc, true));
                    }
                    break;
                case ClientDocumentType.TAX_DOCUMENT:
                    documents.push({
                        docSubName: docSubType,
                        document: doc.documentSubType,
                        // documentSubName: doc.documentSubType,
                        documentSubType: doc.name === "TAX DOCUMENT YEAR 1" ? "YEAR_ONE" : "YEAR_TWO",
                        documentType: doc.documentType,
                        path: doc.path,
                        variant: doc.name === "TAX DOCUMENT YEAR 1" ? "firstYear" : "secondYear",
                    });
            }
        });
        return documents;
    };
    const extractGiftorValues = async (initialFormValues: { [p: string]: never[] }) => {
        const clientPolicy = userData && userData.client && userData.client.clientPolicies && userData.client.clientPolicies.length > 0 && userData.client.clientPolicies[0];
        const gwtClients = clientPolicy && clientPolicy.gwtClients;
        const employeeSSN: any = userData && userData.client && (userData.client.employeeSSN || userData.client.blotSSN);

        if (debugMode) {
            await sleep(150);
            console.info({clientPolicy, gwtClients, userData});
        }

        if (userData) {
            Object.assign(initialFormValues, {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                phoneNumber: userData.phoneNumber,

            });

            if (userData.client) {
                Object.assign(initialFormValues, {
                    dob: userData.client.dob,
                    gender: userData.client.gender,
                    employeeSSN: employeeSSN,
                });
            }
        }

        if (gwtClients) {
            Object.assign(initialFormValues, {
                currentKaiZenPolicy: gwtClients.currentKaiZenPolicy ? "1" : "0",
                gen1TranceNo: gwtClients.tranceNo,
                gen1PolicyNumber: gwtClients.policyNumber,
                gen1ICN: gwtClients.giftorInsuranceCarrier,
                gen1DeathBAP: gwtClients.giftorDeathBenefit,
                hasCurrentInsuranceCoverage: gwtClients.insuranceCoverage ? "1" : "0",
                otherCompanyName: gwtClients.otherCompanyName,
                otherType: gwtClients.otherType,
                otherDeathBenefitAmount: gwtClients.otherDeathBenefitAmount,
                otherPolicyNumber: gwtClients.otherPolicyNumber,
                giftorInsurable: gwtClients.insurable ? "1" : "0",
            });
        }

        if (clientPolicy) {
            assetsFields.forEach((field) => {
                initialFormValues[field.name] = clientPolicy && clientPolicy[field.name];
            });
            liabilitiesFields.forEach((field) => {
                initialFormValues[field.name] = clientPolicy && clientPolicy[field.name];
            });

            Object.assign(initialFormValues, {
                //IndividualIncome Section
                indIncomeLastYearAnnualSalary: clientPolicy.annualIncome,
                indIncomeLastYearDividends: clientPolicy.indIncomeLastYearDividends,
                indIncomeLastYearOtherIncome: clientPolicy.indIncomeLastYearOtherIncome,
                indIncomeLastYearTotal: clientPolicy.indIncomeLastYearTotal,

                indIncome1YearsPriorAnnualSalary: clientPolicy.annualIncome1,
                indIncome1YearsPriorDividends: clientPolicy.indIncome1YearsPriorDividends,
                indIncome1YearsPriorOtherIncome: clientPolicy.indIncome1YearsPriorOtherIncome,
                indIncome1YearsPriorTotal: clientPolicy.indIncome1YearsPriorTotal,

                indIncome2YearsPriorAnnualSalary: clientPolicy.annualIncome2,
                indIncome2YearsPriorOtherIncome: clientPolicy.indIncome2YearsPriorOtherIncome,
                indIncome2YearsPriorDividends: clientPolicy.indIncome2YearsPriorDividends,
                indIncome2YearsPriorTotal: clientPolicy.indIncome2YearsPriorTotal,
                indOtherIncomeDetail: clientPolicy.indOtherIncomeDetail || "",
                netWorth: clientPolicy.netWorth || "",
            });

            const identityDocType = clientPolicy.identityType;

            if (clientPolicy.clientDocuments) {
                const identity = clientPolicy.clientDocuments.filter((doc: any) => {
                    // we need to make array more than 1 verification Document option Exist
                    return doc.documentType === ClientDocumentType.IDENTITY_DOCUMENT;
                });


                const taxDocs = clientPolicy.clientDocuments.filter((doc: any) => {
                    // we need to make array more than 1 verification taxDocs option Exist
                    const allowedDocs = ["Tax Returns", "W2’s", "1099’s"];

                    // taxType = doc.documentSubType;

                    return allowedDocs.includes(doc.documentSubType);
                });

                const taxType = va(taxDocs) ? taxDocs[0].documentSubType : '';

                const identityDocs = createDocumentPayload(identity, identityDocType);
                const taxDocument = createDocumentPayload(taxDocs, taxType);

                debugMode && console.info({identityDocs, identityDocType, taxDocument, taxType});


                Object.assign(initialFormValues, {
                    [fields.identityDocs]: identityDocs,
                    [fields.documentType]: identityDocType,
                    [fields.taxDocument]: taxDocument,
                    [fields.taxDocumentType]: taxType,
                });
            }
        }
    }
    const onSubmit = async (values: any, {setSubmitting, resetForm}: FormikHelpers<any>) => {
        try {
            const gen1Payload = createGiftorPayload(values);

            const payload = {...values, ...gen1Payload};

            await giftorActions.onGiftorConfirm(payload);
            setSubmitting(false);

            resetForm();

            // force status update
            handleEditStatus(GiftorStatus.Confirmed);
        } catch (e) {
            debugMode && console.error(e);
            setSubmitting(false);
        }
    };

    const _validationSchema: any = isNotIndividualOrMultiLife ? {} : createKeyValidation();

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            const initialFormValues = {
                [fields.identityDocs]: [],
                [fields.taxDocument]: [],
            };
            await extractGiftorValues(initialFormValues);
            setInitVal(initialFormValues);
            await sleep(200);
            setLoading(false);
        })();
    }, [user, clientDetail, userData]);

    return (
            <Card className="boxShadow mt20">
                <CardContent>
                    {
                            initVal && validObj(initVal) && initVal.firstName && initVal.lastName && initVal.email &&
                            <Formik
                                    initialValues={initVal}
                                    onSubmit={onSubmit}
                                    validationSchema={yup.object().shape(_validationSchema)}
                            >
                                {
                                    ({errors, handleSubmit, isSubmitting, isValid, initialValues, values}) => (
                                            <form onSubmit={handleSubmit}>
                                                {
                                                        debugMode &&
                                                        <pre>{
                                                            JSON.stringify({
                                                                errors,
                                                                identityDocs: initialValues && initialValues.identityDocs,
                                                                taxDocument: initialValues && initialValues.taxDocument,
                                                                values,
                                                            }, null, 2)
                                                        }</pre>
                                                }
                                                <GiftorPrimaryDetails/>
                                                <GiftorDetails enrollmentID={enrollment && enrollment.id}/>
                                                {
                                                        submitButton &&
                                                        <Button
                                                                className="floatRight mt20 mb20"
                                                                type="submit"
                                                                variant="contained"
                                                                color="primary"
                                                                disabled={isSubmitting || !isValid}
                                                        >
                                                            Confirm Giftor Details
                                                        </Button>
                                                }
                                            </form>
                                    )
                                }
                            </Formik>
                    }

                    {
                            !submitButton &&
                            <Button
                                    className="floatRight mt20 mb20"
                                    variant="contained"
                                    color="primary"
                                    onClick={onCloseGiftor}
                            >
                                Close Giftor Details
                            </Button>
                    }

                </CardContent>
            </Card>
    );
}
